import { ExternalEmployeeStatus, UserRole } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { difference, uniq } from "~/lib/lodash";
import { dangerouslyIgnorePrismaRestrictions } from "~/lib/prismaTokens";
import { getId } from "~/lib/utils";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";

export const fetchExternalEmployeeManageeIds = async (
  ctx: AppContext,
  params: { managerExternalEmployeeId: number; dangerouslyIgnorePrismaRestrictions?: boolean }
) => {
  const externalEmployeesManagees = await recursivelyFetchExternalEmployeeManageeIds(ctx, {
    managerExternalEmployeeIds: [params.managerExternalEmployeeId],
    dangerouslyIgnorePrismaRestrictions: params.dangerouslyIgnorePrismaRestrictions,
  });

  // the .filter is a prevention if there is a cyclic manager organisation
  // if A manages B, B manages C, C manages A
  return externalEmployeesManagees.filter((id) => id !== params.managerExternalEmployeeId);
};

export const fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin = async (
  ctx: AppContext,
  params: { user: NullableAuthenticatedUser; dangerouslyIgnorePrismaRestrictions?: boolean }
) => {
  const { user, dangerouslyIgnorePrismaRestrictions = false } = params;

  if (user?.isSuperAdmin) {
    return null;
  }

  if (
    (user?.permissions.role === UserRole.ADMIN && !dangerouslyIgnorePrismaRestrictions) ||
    (user?.permissions.role === UserRole.HR && !dangerouslyIgnorePrismaRestrictions)
  ) {
    return null;
  }

  return fetchUserExternalEmployeeManageeIds(ctx, params);
};

export const fetchUserExternalEmployeeManageeIds = async (
  ctx: AppContext,
  params: { user: NullableAuthenticatedUser; dangerouslyIgnorePrismaRestrictions?: boolean }
) => {
  const { user } = params;

  if (!user?.permissions.isManager) {
    return null;
  }

  if (!user?.permissions.externalEmployeeId) {
    return [];
  }

  return fetchExternalEmployeeManageeIds(ctx, {
    managerExternalEmployeeId: user.permissions.externalEmployeeId,
    dangerouslyIgnorePrismaRestrictions: params.dangerouslyIgnorePrismaRestrictions,
  });
};

export type UserExternalEmployeeManageeIds = AsyncReturnType<
  typeof fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin
>;

const recursivelyFetchExternalEmployeeManageeIds = async (
  ctx: AppContext,
  params: {
    managerExternalEmployeeIds: number[];
    dangerouslyIgnorePrismaRestrictions?: boolean;
  }
): Promise<number[]> => {
  const externalEmployees = await ctx.prisma.externalEmployee.findMany({
    ...(params.dangerouslyIgnorePrismaRestrictions && { ...dangerouslyIgnorePrismaRestrictions() }),
    where: {
      managerExternalEmployeeId: { in: params.managerExternalEmployeeIds },
      status: { not: ExternalEmployeeStatus.SKIPPED },
    },
    select: { id: true },
  });

  const externalEmployeeIds = externalEmployees.map(getId);

  if (difference(externalEmployeeIds, params.managerExternalEmployeeIds).length > 0) {
    return recursivelyFetchExternalEmployeeManageeIds(ctx, {
      managerExternalEmployeeIds: uniq([...externalEmployeeIds, ...params.managerExternalEmployeeIds]),
      dangerouslyIgnorePrismaRestrictions: params.dangerouslyIgnorePrismaRestrictions,
    });
  }

  return externalEmployees.map(getId);
};
